import clsx from 'clsx';
import { Heart, XIcon } from 'lucide-preact';
import { ComponentProps } from 'preact';
import { useDispatch } from 'react-redux';

import { Button } from '@/components/Buttons';
import { FollowHabitatTrackingData, useFollowHabitat } from '@/hooks/habitats';
import { useSelector } from '@/redux/helper';

import { setMembersOnlyWarningModalState } from '../MembersOnlyWarningModal/redux/actions';

interface FollowButtonProps extends ComponentProps<typeof Button> {
  habitatId: string;
  trackingData?: Omit<FollowHabitatTrackingData, 'habitatId'>;
}

const FollowButton = ({ habitatId, trackingData, onClick: onClickProp, className, ...props }: FollowButtonProps) => {
  const dispatch = useDispatch();
  const isLoggedIn = useSelector((state) => state.user.logged);
  const { isFavorited, follow, unfollow, isError } = useFollowHabitat(habitatId, {
    trackingData: {
      habitatId,
      ...trackingData,
    },
  });

  const onClick: ComponentProps<typeof Button>['onClick'] = (e) => {
    if (!isLoggedIn) {
      dispatch(setMembersOnlyWarningModalState({ type: 'habitat' }));
      onClickProp?.(e);
      return;
    }

    if (isFavorited) {
      unfollow();
    } else {
      follow();
    }
    onClickProp?.(e);
  };

  return (
    <Button
      variant="tertiary"
      onClick={onClick}
      className={clsx(
        'shadow-xs',
        isFavorited
          ? 'bg-tea-100 text-forest-dark hover:bg-tea-200 focus-visible:bg-tea-200 focus-visible:ring-forest-dark'
          : undefined,
        className,
      )}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    >
      {isError ? (
        <XIcon className="size-4 text-system-red" />
      ) : (
        <Heart className={isFavorited ? 'fill-current' : undefined} />
      )}
      {isError ? 'Try again' : isFavorited ? 'Following' : 'Follow'}
    </Button>
  );
};

export default FollowButton;
